import { useMemo, useState } from "react";
import { Bar, BarChart, CartesianGrid, Cell, ReferenceLine, XAxis, YAxis } from "recharts";
import { ChartContainer, ChartTooltip, type ChartConfig } from "@/components/ui/chart";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useLocalization } from "@/context/localization-context";
import { cn } from "@/lib/utils";
import { EmptyBlock, ErrorBlock, FetchingDot, LoadingBlock } from "@/monitoring/components/primitives";
import { StatTile } from "@/monitoring/tabs/backend/stat-tile";
import { NEUTRAL } from "@/monitoring/palette";
import { formatCompact, formatCount } from "@/monitoring/format";
import {
  barFires,
  binKey,
  firesAt,
  fromLog,
  histogramBars,
  logDomainMax,
  logTicks,
  prefersLogScale,
  sameValue,
  thresholdPosition,
  tickKeys,
  toLog,
  type HistogramBar,
} from "../model";
import type { EvaluationSetting, MetricDistribution } from "../types";
import type { SettingText } from "../use-setting-text";

const CHART_HEIGHT = 172;

export interface DistributionState {
  dist: MetricDistribution | undefined;
  loading: boolean;
  fetching: boolean;
  /** the previous window's data is shown while the new one loads */
  stale: boolean;
  error: string | null;
  onRetry: () => void;
  /** "Last 7 days" */
  windowLabel: string;
}

/**
 * Histogram of the metric a threshold cuts, with the edited threshold as a solid line and the saved one
 * dashed when they differ. Bars on the firing side carry the group's colour, the passing side stays grey.
 * Under it: how many pizzas fire at the edited value and how that differs from the saved one.
 */
export function DistributionPanel({
  setting,
  value,
  saved,
  failColor,
  outcome,
  shadow,
  state,
  text,
}: {
  setting: EvaluationSetting;
  /** the edited threshold */
  value: number;
  /** the saved threshold */
  saved: number;
  failColor: string;
  /** what firing means ("Invalid photo") */
  outcome: string;
  /** the threshold only records would_fail */
  shadow: boolean;
  state: DistributionState;
  text: SettingText;
}) {
  const { t, tp } = useLocalization();
  const { dist, loading, fetching, stale, error, onRetry, windowLabel } = state;
  const [logChoice, setLogChoice] = useState<boolean | null>(null);
  const log = logChoice ?? (dist ? prefersLogScale(dist) : false);
  const metric = setting.metric;

  let body: JSX.Element;
  if (!metric) {
    body = <EmptyBlock>{t("evalSettings.chart.noMetric")}</EmptyBlock>;
  } else if (error && !dist) {
    body = <ErrorBlock message={error} onRetry={onRetry} />;
  } else if (loading && !dist) {
    body = <LoadingBlock height={CHART_HEIGHT + 96} />;
  } else if (!dist) {
    body = <EmptyBlock>{t("evalSettings.chart.missing", { metric })}</EmptyBlock>;
  } else if (dist.n <= 0) {
    body = (
      <EmptyBlock className="flex min-h-[8rem] items-center justify-center px-4">
        {text.metricEmpty(metric) ?? t("evalSettings.chart.empty")}
      </EmptyBlock>
    );
  } else {
    body = (
      <>
        <Histogram
          setting={setting}
          dist={dist}
          value={value}
          saved={saved}
          log={log}
          failColor={failColor}
          outcome={outcome}
          shadow={shadow}
          text={text}
        />
        <Legend failColor={failColor} outcome={outcome} shadow={shadow} savedLabel={!sameValue(value, saved) ? text.value(setting.key, saved) : null} />
        <Numbers
          setting={setting}
          dist={dist}
          value={value}
          saved={saved}
          outcome={shadow ? t("evalSettings.chart.shadow", { outcome }) : outcome}
          text={text}
        />
      </>
    );
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-2.5 transition-opacity", stale && "opacity-60")}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-0.5 text-xs text-muted-foreground">
          <div className="flex min-w-0 flex-wrap items-baseline gap-x-1.5">
            <span className="font-medium text-foreground">{t("evalSettings.chart.title")}</span>
            {metric && <span className="break-all font-mono text-[11px]">{metric}</span>}
            <FetchingDot active={fetching} />
          </div>
          <div className="flex min-w-0 flex-wrap items-center gap-x-1.5">
            <span>{windowLabel}</span>
            {dist && dist.n > 0 && (
              <>
                <span aria-hidden>·</span>
                <span className="tabular-nums">{tp("count.pizzas", dist.n, { count: formatCount(dist.n) })}</span>
              </>
            )}
          </div>
        </div>
        {dist && dist.n > 0 && (
          <ToggleGroup
            type="single"
            size="sm"
            variant="outline"
            value={log ? "log" : "lin"}
            onValueChange={(next) => next && setLogChoice(next === "log")}
            aria-label={t("evalSettings.chart.scale")}
            className="shrink-0 gap-0.5"
          >
            <ToggleGroupItem value="lin" className="h-8 px-2.5 text-xs" title={t("evalSettings.chart.linearLong")} aria-label={t("evalSettings.chart.linearLong")}>
              {t("evalSettings.chart.linear")}
            </ToggleGroupItem>
            <ToggleGroupItem value="log" className="h-8 px-2.5 text-xs" title={t("evalSettings.chart.logLong")} aria-label={t("evalSettings.chart.logLong")}>
              {t("evalSettings.chart.log")}
            </ToggleGroupItem>
          </ToggleGroup>
        )}
      </div>
      {body}
    </div>
  );
}

interface Row {
  key: string;
  y: number;
  count: number;
  fires: boolean;
}

function Histogram({
  setting,
  dist,
  value,
  saved,
  log,
  failColor,
  outcome,
  shadow,
  text,
}: {
  setting: EvaluationSetting;
  dist: MetricDistribution;
  value: number;
  saved: number;
  log: boolean;
  failColor: string;
  outcome: string;
  shadow: boolean;
  text: SettingText;
}) {
  const { t } = useLocalization();
  const bars = useMemo(() => histogramBars(dist), [dist]);
  const barByKey = useMemo(() => new Map(bars.map((bar) => [bar.key, bar])), [bars]);
  const maxCount = useMemo(() => Math.max(1, ...bars.map((bar) => bar.count)), [bars]);
  const yTicks = useMemo(() => (log ? logTicks(maxCount) : undefined), [log, maxCount]);
  // tick labels sit on the bin edges: the start edge of every n-th bin and the closing edge of the last one
  const { ticks, startTicks, lastKey } = useMemo(() => {
    const starts = tickKeys(dist);
    const last = binKey(dist.counts.length - 1);
    return { ticks: starts.includes(last) ? starts : [...starts, last], startTicks: new Set(starts), lastKey: last };
  }, [dist]);

  const rows: Row[] = bars.map((bar) => ({
    key: bar.key,
    y: log ? toLog(bar.count) : bar.count,
    count: bar.count,
    fires: barFires(bar, dist, value),
  }));
  const edited = thresholdPosition(dist, bars, value);
  const savedAt = sameValue(value, saved) ? null : thresholdPosition(dist, bars, saved);
  const metricName = setting.metric ? text.metricLabel(setting.metric) : "x";
  const edge = (v: number) => text.percent(v, dist.width);
  const config: ChartConfig = { y: { label: outcome, color: failColor } };

  const rangeText = (bar: HistogramBar) => {
    const gte = dist.fail_when === "gte";
    if (bar.kind === "under") return `${metricName} ${gte ? "<" : "≤"} ${edge(dist.lo)}`;
    if (bar.kind === "over") return `${metricName} ${gte ? "≥" : ">"} ${edge(dist.hi)}`;
    return gte
      ? `${edge(bar.start!)} ≤ ${metricName} < ${edge(bar.end!)}`
      : `${edge(bar.start!)} < ${metricName} ≤ ${edge(bar.end!)}`;
  };

  return (
    <ChartContainer
      config={config}
      className="aspect-auto w-full"
      style={{ height: CHART_HEIGHT }}
      role="img"
      aria-label={t("evalSettings.chart.label", { metric: metricName })}
    >
      <BarChart data={rows} margin={{ top: 18, right: 14, bottom: 0, left: 0 }} barCategoryGap={1}>
        <CartesianGrid vertical={false} strokeDasharray="3 3" className="stroke-border/60" />
        <XAxis
          dataKey="key"
          ticks={ticks}
          interval={0}
          tickLine={false}
          axisLine={{ stroke: "hsl(var(--border))" }}
          height={20}
          tick={(props: { x: number; y: number; payload: { value: string; offset?: number } }) => {
            const { x, y, payload } = props;
            const bar = barByKey.get(payload.value);
            if (!bar || bar.kind !== "bin") return <g />;
            const half = payload.offset ?? 0;
            return (
              <g>
                {startTicks.has(bar.key) && (
                  <text x={x - half} y={y + 10} textAnchor="middle" className="fill-muted-foreground text-[10px]">
                    {edge(bar.start!)}
                  </text>
                )}
                {bar.key === lastKey && (
                  <text x={x + half} y={y + 10} textAnchor="end" className="fill-muted-foreground text-[10px]">
                    {edge(bar.end!)}
                  </text>
                )}
              </g>
            );
          }}
        />
        <YAxis
          width={34}
          tickLine={false}
          axisLine={false}
          tickMargin={4}
          allowDecimals={false}
          ticks={yTicks}
          domain={log ? [0, logDomainMax(maxCount)] : [0, "auto"]}
          tickFormatter={(v: number) => formatCompact(log ? fromLog(v) : v)}
          className="text-[10px]"
        />
        <ChartTooltip
          cursor={{ className: "fill-muted/40" }}
          isAnimationActive={false}
          content={({ active, payload }) => {
            const row = payload?.[0]?.payload as Row | undefined;
            const bar = row ? barByKey.get(row.key) : undefined;
            if (!active || !row || !bar) return null;
            return (
              <div className="min-w-[11rem] rounded-md border bg-popover px-2.5 py-2 text-xs shadow-md">
                <div className="mb-1 font-medium tabular-nums text-foreground">{rangeText(bar)}</div>
                <div className="flex items-baseline justify-between gap-3">
                  <span className="font-mono tabular-nums text-foreground">{formatCount(row.count)}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {text.percent(dist.n ? row.count / dist.n : 0, 0.001)}
                  </span>
                </div>
                <div className="mt-1 flex items-center gap-1.5 text-muted-foreground">
                  <span
                    className="inline-block h-2.5 w-2.5 shrink-0 rounded-[3px]"
                    style={{ backgroundColor: row.fires ? failColor : NEUTRAL.mid, opacity: row.fires && shadow ? 0.5 : 1 }}
                  />
                  {row.fires ? (shadow ? t("evalSettings.chart.shadow", { outcome }) : outcome) : t("evalSettings.chart.passes")}
                </div>
              </div>
            );
          }}
        />
        <Bar
          dataKey="y"
          isAnimationActive={false}
          radius={[2, 2, 0, 0]}
          minPointSize={(v) => (typeof v === "number" && v > 0 ? 2 : 0)}
        >
          {rows.map((row) => (
            <Cell
              key={row.key}
              fill={row.fires ? failColor : NEUTRAL.mid}
              fillOpacity={row.fires && shadow ? 0.5 : 1}
            />
          ))}
        </Bar>
        {savedAt && (
          <ReferenceLine
            x={savedAt.x}
            position={savedAt.position}
            stroke={NEUTRAL.baseline}
            strokeWidth={1.5}
            strokeDasharray="4 3"
          />
        )}
        {edited && (
          <ReferenceLine
            x={edited.x}
            position={edited.position}
            stroke="hsl(var(--foreground))"
            strokeWidth={1.5}
            label={{
              value: text.value(setting.key, value),
              position: "top",
              fontSize: 10,
              fontWeight: 600,
              fill: "hsl(var(--foreground))",
            }}
          />
        )}
      </BarChart>
    </ChartContainer>
  );
}

function Legend({
  failColor,
  outcome,
  shadow,
  savedLabel,
}: {
  failColor: string;
  outcome: string;
  shadow: boolean;
  /** the saved threshold, when the edited one differs */
  savedLabel: string | null;
}) {
  const { t } = useLocalization();
  return (
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-muted-foreground">
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: failColor, opacity: shadow ? 0.5 : 1 }} />
        {shadow ? t("evalSettings.chart.shadow", { outcome }) : outcome}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-2.5 w-2.5 rounded-[3px]" style={{ backgroundColor: NEUTRAL.mid }} />
        {t("evalSettings.chart.passes")}
      </span>
      <span className="inline-flex items-center gap-1.5">
        <span className="inline-block h-3 w-0 border-l-[1.5px] border-foreground" />
        {t("evalSettings.chart.threshold")}
      </span>
      {savedLabel && (
        <span className="inline-flex items-center gap-1.5">
          <span className="inline-block h-3 w-0 border-l-[1.5px] border-dashed" style={{ borderColor: NEUTRAL.baseline }} />
          {t("evalSettings.chart.saved")} {savedLabel}
        </span>
      )}
    </div>
  );
}

function Numbers({
  setting,
  dist,
  value,
  saved,
  outcome,
  text,
}: {
  setting: EvaluationSetting;
  dist: MetricDistribution;
  value: number;
  saved: number;
  outcome: string;
  text: SettingText;
}) {
  const { t, formatNumber } = useLocalization();
  const atValue = firesAt(dist, value);
  const atSaved = firesAt(dist, saved);
  const share = (count: number) => (dist.n ? count / dist.n : 0);
  const changed = !sameValue(value, saved);
  const delta = atValue - atSaved;
  const deltaPp = (share(atValue) - share(atSaved)) * 100;
  const q = dist.quantiles;
  const quantile = (v: number | null | undefined) => (v === null || v === undefined ? "–" : text.percent(v, dist.width));
  const versions = Object.entries(dist.versions ?? {}).sort((a, b) => b[1] - a[1]);

  return (
    <div className="flex flex-col gap-2">
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
        <StatTile
          size="sm"
          label={t("evalSettings.stats.current", { outcome, rule: text.shortRule(setting, value) })}
          value={<span className="tabular-nums">{formatCount(atValue)}</span>}
          sub={t("evalSettings.chart.share", { share: text.percent(share(atValue), 0.001), n: formatCount(dist.n) })}
        />
        <StatTile
          size="sm"
          label={t("evalSettings.stats.vsSaved", { rule: text.shortRule(setting, saved) })}
          value={
            <span className={cn("tabular-nums", !changed && "text-muted-foreground")}>
              {changed ? formatNumber(delta, { signDisplay: "exceptZero" }) : "±0"}
            </span>
          }
          sub={
            changed
              ? t("evalSettings.stats.pp", {
                  value: formatNumber(deltaPp, { signDisplay: "exceptZero", maximumFractionDigits: 1 }),
                })
              : t("evalSettings.stats.noChange")
          }
        />
        <StatTile
          size="sm"
          className="col-span-2 sm:col-span-1"
          label={t("evalSettings.stats.quantiles")}
          value={
            <span className="tabular-nums">
              {quantile(q?.p50)} <span className="text-muted-foreground">·</span> {quantile(q?.p90)}{" "}
              <span className="text-muted-foreground">·</span> {quantile(q?.p99)}
            </span>
          }
        />
      </div>
      {(dist.zero > 0 || versions.length > 1) && (
        <div className="flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-muted-foreground">
          {dist.zero > 0 && (
            <span className="tabular-nums">
              {t("evalSettings.stats.zero", {
                count: formatCount(dist.zero),
                share: text.percent(share(dist.zero), 0.001),
              })}
            </span>
          )}
          {versions.length > 1 && (
            <span className="tabular-nums">
              {t("evalSettings.stats.versions", {
                list: versions.map(([version, count]) => `${version} ${formatCount(count)}`).join(" · "),
              })}
            </span>
          )}
        </div>
      )}
    </div>
  );
}
